import React from 'react';
import ReactMarkdown, { Options as ReactMarkdownOptions } from 'react-markdown';
import { Box, List, ListItem, Theme, Typography, makeStyles } from '@material-ui/core';
import { Link } from './Link';
import { isString } from './typeInference';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const remarkGfm = require('remark-gfm');

const useStyles = makeStyles((theme: Theme) => ({
    markdownContainer: {
        '& > table': {
            borderSpacing: 0,
            borderCollapse: 'collapse',
            display: 'block',
            marginTop: '8px',
            marginBottom: '16px',
            width: 'max-content',
            maxWidth: '100%',
            overflow: 'auto',
            '& > tbody > tr': {
                '& > td,th': {
                    padding: '6px 13px',
                    border: `1px solid ${theme.palette.divider}`
                }
            },
            '& > thead > tr': {
                '& > td,th': {
                    padding: '6px 13px',
                    border: `1px solid ${theme.palette.divider}`
                }
            }
        }
    }
}));

interface IMarkdownProps {
    source: string;
    className?: string;
}

// How should certain things be rendered with Material UI - some native html tags do not work as expected
// This library has garbage typing. Good luck.
const renderers: ReactMarkdownOptions['components'] = {
    p: ({ children }) => <Typography>{children}</Typography>,
    strong: ({ children }) => <b>{children}</b>,
    em: ({ children }) => <span style={{ fontStyle: 'italic' }}>{children}</span>,
    h1: ({ children }) => <Typography variant="h1">{children}</Typography>,
    h2: ({ children }) => <Typography variant="h2">{children}</Typography>,
    h3: ({ children }) => <Typography variant="h3">{children}</Typography>,
    h4: ({ children }) => <Typography variant="h4">{children}</Typography>,
    h5: ({ children }) => <Typography variant="h5">{children}</Typography>,
    h6: ({ children }) => <Typography variant="h6">{children}</Typography>,
    ul: ({ children }) => <List>{children}</List>,
    li: ({ children }) => <ListItem style={{ flexDirection: 'column' }}>{children}</ListItem>,
    code: ({ children }) => <code style={{ maxWidth: '100%', whiteSpace: 'break-spaces' }}>{children}</code>,
    image: ({ children }) => (
        <img src={isString(children) ? children : undefined} style={{ maxWidth: '100%' }} />
    ),
    a: ({ href, children }) => <Link to={href ?? ''}>{children}</Link>
};

// When developing this module, enable this. The template has all variations of Markdown
const useTemplate = false;
const template = `
# h1 Heading 8-)
## h2 Heading
### h3 Heading
#### h4 Heading
##### h5 Heading
###### h6 Heading


## Horizontal Rules

___

---

***


## Typographic replacements

Enable typographer option to see result.

(c) (C) (r) (R) (tm) (TM) (p) (P) +-

test.. test... test..... test?..... test!....

!!!!!! ???? ,,  -- ---

"Smartypants, double quotes" and 'single quotes'


## Emphasis

**This is bold text**

__This is bold text__

*This is italic text*

_This is italic text_

~~Strikethrough~~


## Blockquotes


> Blockquotes can also be nested...
>> ...by using additional greater-than signs right next to each other...
> > > ...or with spaces between arrows.


## Lists

Unordered

+ Create a list by starting a line with \`+\`, \`-\`, or \`*\`
+ Sub-lists are made by indenting 2 spaces:
  - Marker character change forces new list start:
    * Ac tristique libero volutpat at
    + Facilisis in pretium nisl aliquet
    - Nulla volutpat aliquam velit
+ Very easy!

Ordered

1. Lorem ipsum dolor sit amet
2. Consectetur adipiscing elit
3. Integer molestie lorem at massa


1. You can use sequential numbers...
1. ...or keep all the numbers as \`1.\`

Start numbering with offset:

57. foo
1. bar


## Code

Inline \`code\`

Indented code

    // Some comments
    line 1 of code
    line 2 of code
    line 3 of code


Block code "fences"

\`\`\`
Sample text here...
\`\`\`

Syntax highlighting

\`\`\` js
var foo = function (bar) {
  return bar++;
};

console.log(foo(5));
\`\`\`

## Tables

| Option | Description |
| ------ | ----------- |
| data   | path to data files to supply the data that will be passed into templates. |
| engine | engine to be used for processing templates. Handlebars is the default. |
| ext    | extension to be used for dest files. |

Right aligned columns

| Option | Description |
| ------:| -----------:|
| data   | path to data files to supply the data that will be passed into templates. |
| engine | engine to be used for processing templates. Handlebars is the default. |
| ext    | extension to be used for dest files. |


## Links

[link text](http://dev.nodeca.com)

[link with title](http://nodeca.github.io/pica/demo/ "title text!")

Autoconverted link https://github.com/nodeca/pica (enable linkify to see)


## Images

![Minion](https://octodex.github.com/images/minion.png)
![Stormtroopocat](https://octodex.github.com/images/stormtroopocat.jpg "The Stormtroopocat")

Like links, Images also have a footnote style syntax

![Alt text][id]

With a reference later in the document defining the URL location:

[id]: https://octodex.github.com/images/dojocat.jpg  "The Dojocat"


## Plugins

The killer feature of \`markdown-it\` is very effective support of
[syntax plugins](https://www.npmjs.org/browse/keyword/markdown-it-plugin).


### [Emojies](https://github.com/markdown-it/markdown-it-emoji)

> Classic markup: :wink: :crush: :cry: :tear: :laughing: :yum:
>
> Shortcuts (emoticons): :-) :-( 8-) ;)

see [how to change output](https://github.com/markdown-it/markdown-it-emoji#change-output) with twemoji.


### [Subscript](https://github.com/markdown-it/markdown-it-sub) / [Superscript](https://github.com/markdown-it/markdown-it-sup)

- 19^th^
- H~2~O


### [\\<ins>](https://github.com/markdown-it/markdown-it-ins)

++Inserted text++


### [\\<mark>](https://github.com/markdown-it/markdown-it-mark)

==Marked text==


### [Footnotes](https://github.com/markdown-it/markdown-it-footnote)

Footnote 1 link[^first].

Footnote 2 link[^second].

Inline footnote^[Text of inline footnote] definition.

Duplicated footnote reference[^second].

[^first]: Footnote **can have markup**

    and multiple paragraphs.

[^second]: Footnote text.


### [Definition lists](https://github.com/markdown-it/markdown-it-deflist)

Term 1

:   Definition 1
with lazy continuation.

Term 2 with *inline markup*

:   Definition 2

        { some code, part of Definition 2 }

    Third paragraph of definition 2.

_Compact style:_

Term 1
  ~ Definition 1

Term 2
  ~ Definition 2a
  ~ Definition 2b


### [Abbreviations](https://github.com/markdown-it/markdown-it-abbr)

This is HTML abbreviation example.

It converts "HTML", but keep intact partial entries like "xxxHTMLyyy" and so on.

*[HTML]: Hyper Text Markup Language

### [Custom containers](https://github.com/markdown-it/markdown-it-container)

::: warning
*here be dragons*
:::
`;

export const Markdown: React.FC<IMarkdownProps> = props => {
    const { source, className } = props;
    const classes = useStyles();
    return (
        <Box className={classes.markdownContainer}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} className={className} components={renderers}>
                {useTemplate ? template : source}
            </ReactMarkdown>
        </Box>
    );
};
